import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { Asset } from "@yushi-portfolio/content-schema";
import type { ApiClient } from "./api";
import { assetStatus } from "./upload";

type AssetsState = {
  assets: Asset[] | undefined;
  byId: Map<string, Asset>;
  error?: string;
  refresh(): Promise<void>;
  /** Adds or replaces one asset locally (after upload or edit). */
  upsert(asset: Asset): void;
  mediaBaseUrl: string;
};

const AssetsContext = createContext<AssetsState | undefined>(undefined);
const POLL_MS = 4000;

export function AssetsProvider({ api, mediaBaseUrl, children }: { api: ApiClient; mediaBaseUrl: string; children: ReactNode }) {
  const [assets, setAssets] = useState<Asset[]>();
  const [error, setError] = useState<string>();
  const apiRef = useRef(api);

  const refresh = useCallback(async () => {
    try {
      setAssets((await apiRef.current.listAssets()).items);
      setError(undefined);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // While anything is still being processed, poll until variants appear.
  const processing = assets?.some((a) => assetStatus(a) === "processing") ?? false;
  useEffect(() => {
    if (!processing) return;
    const timer = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [processing, refresh]);

  const upsert = useCallback((asset: Asset) => {
    setAssets((current) => [asset, ...(current ?? []).filter((a) => a.id !== asset.id)]);
  }, []);

  const value = useMemo(
    () => ({ assets, byId: new Map((assets ?? []).map((a) => [a.id, a])), error, refresh, upsert, mediaBaseUrl }),
    [assets, error, refresh, upsert, mediaBaseUrl],
  );
  return <AssetsContext.Provider value={value}>{children}</AssetsContext.Provider>;
}

export function useAssets(): AssetsState {
  const value = useContext(AssetsContext);
  if (!value) throw new Error("useAssets outside AssetsProvider");
  return value;
}
